import React from 'react';
import JxMinimizableFormBase from './JxMinimizableFormBase';
import './JxFormResizableBase.css';
import { publish } from '../../services/utils/pubsub';
import { JxWindowEvents } from '../JxWindowManager/JxWindowEvents';
import { clampSize } from './jxFormGeoUtils';

const CURSORS = {
    nw: 'nwse-resize',
    ne: 'nesw-resize',
    sw: 'nesw-resize',
    se: 'nwse-resize',
    n: 'ns-resize',
    s: 'ns-resize',
    e: 'ew-resize',
    w: 'ew-resize',
};

const HANDLES = ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];

/**
 * JxFormResizableBase — resize-from-corner and resize-from-edge behavior layered on top of JxForm.
 * Chain: CoreComponent → JxFormValidationBase → JxFormBase → JxFormDraggableBase → JxForm →
 *        JxMinimizableFormBase → JxFormResizableBase
 * Extends JxMinimizableFormBase (not JxForm directly, 2026-07-18) so resizable widgets get
 * minimize/taskbar plumbing available for free — harmless for ones that never pass
 * isMinimizable to JxTitleBar, they simply never receive a 'minimize' notify.
 *
 * Geo itself (initial resolution: cache-or-default) is owned by JxFormBase.componentDidMount —
 * this class only ever reads/writes state.geo.width/height (and top/left on corner-resize) live.
 *
 * NOTE: uses _onResizeMouseDown (not _onMouseDown) to avoid shadowing JxFormDraggableBase drag handler.
 * NOTE: mousemove/mouseup use capture phase so they fire before stopPropagation on child elements.
 *
 * Corners nw/ne/sw/se — diagonal resize, two axes simultaneously.
 * Edges n/s/e/w — single-axis resize, opposite edge stays anchored.
 */
export default class JxFormResizableBase extends JxMinimizableFormBase {
    state = {
        ...this.state, // geo, dragging, zIndex from JxFormBase/JxFormDraggableBase
        resizing: false,
    };

    _resizeStart = { x: 0, y: 0, width: 0, height: 0, posLeft: 0, posTop: 0 };

    /**
     * Extends JxFormDraggableBase.panelStyle with width + height from state.geo.
     */
    get panelStyle() {
        return {
            ...super.panelStyle, // top, left, zIndex
            width: this.state.geo.width,
            height: this.state.geo.height,
        };
    }

    _onResizeMouseDown = (handle, e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const { top, left, width, height } = this.state.geo;
        this._resizeStart = { x: e.clientX, y: e.clientY, width, height, posLeft: left, posTop: top };
        this.setState({ resizing: true });

        const onMouseMove = (ev) => {
            ev.stopImmediatePropagation();
            const dx = ev.clientX - this._resizeStart.x;
            const dy = ev.clientY - this._resizeStart.y;

            const newSize = { width: this._resizeStart.width, height: this._resizeStart.height };
            const newPos = { left: this._resizeStart.posLeft, top: this._resizeStart.posTop };

            if (handle === 'e' || handle === 'se' || handle === 'ne') newSize.width = this._resizeStart.width + dx;
            if (handle === 'w' || handle === 'sw' || handle === 'nw') newSize.width = this._resizeStart.width - dx;
            if (handle === 's' || handle === 'se' || handle === 'sw') newSize.height = this._resizeStart.height + dy;
            if (handle === 'n' || handle === 'ne' || handle === 'nw') newSize.height = this._resizeStart.height - dy;

            const clamped = clampSize(newSize);

            if (handle === 'w' || handle === 'sw' || handle === 'nw')
                newPos.left = this._resizeStart.posLeft + (this._resizeStart.width - clamped.width);
            if (handle === 'n' || handle === 'ne' || handle === 'nw')
                newPos.top = this._resizeStart.posTop + (this._resizeStart.height - clamped.height);

            this.setState({ geo: { ...this.state.geo, ...clamped, ...newPos } });
        };

        document.body.style.userSelect = 'none';
        document.body.classList.add('jx-dragging');
        document.body.style.cursor = CURSORS[handle];

        const onMouseUp = () => {
            document.body.style.userSelect = '';
            document.body.classList.remove('jx-dragging');
            document.body.style.cursor = '';
            this.setState({ resizing: false });
            window.removeEventListener('mousemove', onMouseMove, true);
            window.removeEventListener('mouseup', onMouseUp, true);
            this.publishWindowSize();
        };

        window.addEventListener('mousemove', onMouseMove, true);
        window.addEventListener('mouseup', onMouseUp, true);
    };

    publishWindowSize() {
        try {
            const { top, left, width, height } = this.state.geo;
            publish(JxWindowEvents.channel, {
                name: JxWindowEvents.onSizeChanged,
                target: this.getTarget(),
                data: { posX: left, posY: top, height, width, component: this.getObjectType() },
            });
        } catch (error) {
            this.logError('publishWindowSize', error.message);
        }
    }

    getResizeHandleProps(handle) {
        return {
            onMouseDown: (e) => this._onResizeMouseDown(handle, e),
            style: { cursor: CURSORS[handle] },
        };
    }

    /**
     * Override to exclude specific handles that conflict with panel content.
     * Example: return ['w'] when a click strip occupies the left edge.
     * @returns {string[]}
     */
    getDisabledHandles() {
        return [];
    }

    renderResizeHandles() {
        const disabled = new Set(this.getDisabledHandles());
        return HANDLES.filter((h) => !disabled.has(h)).map((handle) =>
            React.createElement('div', {
                key: handle,
                className: `jx-resize-handle jx-resize-${handle}`,
                ...this.getResizeHandleProps(handle),
            }),
        );
    }
}
